import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, Observable, tap } from 'rxjs';
import { PI } from '../constants-types';
import { ConfigurationService } from '../system-configuration';
import { LogoutMessage, MessagingService } from '../system-services';
import { Auth0SdkAuthenticationService } from './Auth0SdkAuthentication.service';
import { AuthenticatedUser, AuthenticationProxy } from './AuthenticationProxy';
@Injectable({
    providedIn: 'root'
})
export class AuthenticationService implements AuthenticationProxy {
    private authService: AuthenticationProxy;

    constructor(
        sdk: Auth0SdkAuthenticationService,
        private messageService: MessagingService,
        private config: ConfigurationService,
        private http: HttpClient
    ) {
        this.authService = sdk;
        this.messageService
            .listenFor<LogoutMessage>(LogoutMessage)
            .subscribe(() => {
                this.authService.logout();
            });
    }

    clearErrors(): void {
        return this.authService.clearErrors();
    }

    getErrors(): { hasErrors: boolean; errors: Error[] } {
        return this.authService.getErrors();
    }

    getErrorStream(): Observable<Error> {
        return this.authService.getErrorStream();
    }

    logout(): void {
        this.logoutPerform().then().catch();

        this.authService.logout();
    }

    getToken(): Observable<string> {
        return this.authService.getToken();
    }

    isAuthenticated(): Observable<boolean> {
        return this.authService.isAuthenticated().pipe(
            tap(async (authenticated) => {
                if (authenticated) {
                    await this.setLastLoginStatus();
                }
            })
        );
    }

    async goToLogin(): Promise<any> {
        const result = this.getErrors();
        let loginAllowed = true;

        if (result.hasErrors) {
            // Look for an SSO Error
            const isSSOError = this.isSSOError(result.errors);
            this.clearErrors();

            if (isSSOError) {
                loginAllowed = false;
                this.redirectToMarketingPage();
            }
        }

        if (loginAllowed) {
            await this.authService.goToLogin();
        }
    }

    async goToApplication(): Promise<any> {
        await this.authService.goToApplication();
    }

    redirectToTargetPath(): Promise<boolean> {
        return this.authService.redirectToTargetPath();
    }

    getUser(): Observable<AuthenticatedUser> {
        return this.authService.getUser();
    }

    async fetchDeepLink(): Promise<string> {
        return await this.authService.fetchDeepLink();
    }

    private isSSOError(auth0Errors: Error[]): boolean {
        return auth0Errors.find((e) => e.message.startsWith('SSO-'))
            ? true
            : false;
    }

    private redirectToMarketingPage() {
        const marketingPageUrl = new URL(
            `${this.config.getMarketingPageUrl()}/sso/`
        );
        marketingPageUrl.searchParams.append('sso', '1');
        window.location.assign(marketingPageUrl);
    }

    private async setLastLoginStatus() {
        const url = `${this.config.getUrlWithHost(
            '/api/v4/user/updatelastlogintime'
        )}`;
        // This is not using the API utility as it results in a circular dependency.
        return await firstValueFrom(this.http.patch(url, null));
    }

    /**
     * We want to log out from the perform application and destroy the perform session
     * At the same time we log out from the PI application
     *
     * @returns
     */
    private async logoutPerform() {
        return await fetch(PI.appSettings.performUrl + '/user/logout', {
            credentials: 'include'
        });
    }
}
